import { isNil } from 'lodash';
import { UiStorageKey } from 'mbs-ui-shared@models';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { Build, BuildsInfo } from 'mbs-ui-shared@models/build';
import { NewVersionsType, SettingsType, ShowUpdatesAlertType } from './builds.types';

export type VersionsHashCodes = Record<VersionsHashCodeKey, string>;

type VersionsHashCodeKey = UiStorageKey.BackupVersionsHashCode | UiStorageKey.RmmVersionsHashCode | UiStorageKey.RAVersionsHashCode;

type AgentVersionsConfig = {
  storageKey: VersionsHashCodeKey;
  versionsKey: keyof NewVersionsType;
  dotKey: keyof ShowUpdatesAlertType;
};

export const agentVersionsConfig: Partial<Record<string, AgentVersionsConfig>> = {
  [AgentType.Backup]: { storageKey: UiStorageKey.BackupVersionsHashCode, versionsKey: 'backupVersions', dotKey: 'showBackupUpdateDot' },
  [AgentType.RMM]: { storageKey: UiStorageKey.RmmVersionsHashCode, versionsKey: 'rmmVersions', dotKey: 'showRmmUpdateDot' },
  [AgentType.RA]: { storageKey: UiStorageKey.RAVersionsHashCode, versionsKey: 'raVersions', dotKey: 'showRAUpdateDot' }
};

export const noUpdateDots: ShowUpdatesAlertType = {
  showBackupUpdateDot: false,
  showRmmUpdateDot: false,
  showRAUpdateDot: false
};

export function isBuildGenerated(build: Build): boolean {
  return !!(build.public || build.sandbox);
}

export function mergeBuildLists(builds: Build[], update: Build[]): Build[] {
  return builds?.map(
    (available) => update.find((item) => item.mbsBuildType === available.mbsBuildType) ?? { ...available, public: null, sandbox: null }
  );
}

export function getInitialBuildList(availableBuilds: { builds: Build[] }, buildsInfo: BuildsInfo, canRebrand: boolean): Build[] {
  if (!availableBuilds) return buildsInfo.builds;

  const merged = mergeBuildLists(availableBuilds.builds, buildsInfo.builds);

  return canRebrand ? merged : merged.filter(isBuildGenerated);
}

export function sortGeneratedFirst(builds: Build[]): Build[] {
  return [...builds].sort((buildA, buildB) => Number(isBuildGenerated(buildB)) - Number(isBuildGenerated(buildA)));
}

export function extractSettings(buildsInfo: BuildsInfo): SettingsType {
  return {
    isAutoUpdateEnabled: buildsInfo.autoUpdateEnabled,
    isBetaAvailable: buildsInfo.betaAvailable ?? false,
    isSandBoxAvailable: buildsInfo.sandBoxAvailable,
    isSettingsButtonVisible: buildsInfo.settingsButtonVisible,
    isAutoGenerateEnabled: buildsInfo.autoGenerateEnabled ?? false
  };
}

export function getVersionsHashCodes(versions: NewVersionsType, toHashCode: (versions: string[]) => string): VersionsHashCodes {
  return {
    [UiStorageKey.BackupVersionsHashCode]: toHashCode(versions.backupVersions),
    [UiStorageKey.RmmVersionsHashCode]: toHashCode(versions.rmmVersions),
    [UiStorageKey.RAVersionsHashCode]: toHashCode(versions.raVersions)
  };
}

export function getUpdateDots(
  versions: NewVersionsType,
  hashCodes: VersionsHashCodes,
  storage: Record<string, unknown>
): ShowUpdatesAlertType {
  const configs = Object.values(agentVersionsConfig);
  const isNothingSeenYet = configs.every(({ storageKey }) => isNil(storage?.[storageKey]));

  return configs.reduce(
    (dots, { storageKey, versionsKey, dotKey }) => ({
      ...dots,
      [dotKey]: isNothingSeenYet ? !!versions[versionsKey]?.length : hashCodes[storageKey] !== storage?.[storageKey]
    }),
    { ...noUpdateDots }
  );
}
